import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CreditCard, ChevronDown } from 'lucide-react';
import ModalWrapper from '../ModalWrapper';

export default function AddFundsModal({ onClose }) {
    const [paymentMethod, setPaymentMethod] = useState('card');
    const [cardNumber, setCardNumber] = useState('');
    const [expiry, setExpiry] = useState('');
    const [cvc, setCvc] = useState('');
    const [country, setCountry] = useState('');
    const [amount, setAmount] = useState('');
    const [showCountryDropdown, setShowCountryDropdown] = useState(false);

    const countries = ['United States', 'United Kingdom', 'Canada', 'Nigeria', 'Germany', 'France'];

    const formatCardNumber = (value) => {
        const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
        const matches = v.match(/\d{4,16}/g);
        const match = (matches && matches[0]) || '';
        const parts = [];
        for (let i = 0, len = match.length; i < len; i += 4) {
            parts.push(match.substring(i, i + 4));
        }
        return parts.length ? parts.join(' ') : value;
    };

    return (
        <ModalWrapper onClose={onClose} title="Choose payment method">
            <div className="p-4">
                {/* Payment Method Tabs */}
                <div className="flex gap-3 mb-6">
                    <button
                        onClick={() => setPaymentMethod('card')}
                        className={`flex-1 p-4 rounded-lg flex items-center gap-3 transition-all ${
                            paymentMethod === 'card' 
                                ? 'bg-[#c9a227] text-black' 
                                : 'bg-[#4a4a4a] text-white hover:bg-[#5a5a5a]'
                        }`}
                    >
                        <CreditCard className="w-5 h-5" />
                        <span>Card</span>
                    </button>
                    <button
                        onClick={() => setPaymentMethod('google')}
                        className={`flex-1 p-4 rounded-lg flex items-center gap-3 transition-all ${
                            paymentMethod === 'google' 
                                ? 'bg-[#c9a227] text-black' 
                                : 'bg-[#4a4a4a] text-white hover:bg-[#5a5a5a]'
                        }`}
                    >
                        <span className="text-sm font-bold">G Pay</span>
                        <span>Google Pay</span>
                    </button>
                    <button
                        onClick={() => setPaymentMethod('apple')}
                        className={`flex-1 p-4 rounded-lg flex items-center gap-3 transition-all ${
                            paymentMethod === 'apple' 
                                ? 'bg-[#c9a227] text-black' 
                                : 'bg-[#4a4a4a] text-white hover:bg-[#5a5a5a]'
                        }`}
                    >
                        <span className="text-lg"></span>
                        <span>Apple Pay</span>
                    </button>
                </div>

                {paymentMethod === 'card' && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4"
                    >
                        {/* Card Number */}
                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Card Number</label>
                            <input
                                type="text"
                                value={cardNumber}
                                onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
                                placeholder="1234 1234 1234 1234"
                                maxLength={19}
                                className="w-full bg-[#4a4a4a] border border-[#5a5a5a] rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a227]"
                            />
                        </div>

                        {/* Expiry & CVC */}
                        <div className="flex gap-4">
                            <div className="flex-1">
                                <label className="block text-gray-400 text-sm mb-2">Expiry</label>
                                <input
                                    type="text"
                                    value={expiry}
                                    onChange={(e) => setExpiry(e.target.value)}
                                    placeholder="MM / YY"
                                    maxLength={7}
                                    className="w-full bg-[#4a4a4a] border border-[#5a5a5a] rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a227]"
                                />
                            </div>
                            <div className="flex-1">
                                <label className="block text-gray-400 text-sm mb-2">CVC</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={cvc}
                                        onChange={(e) => setCvc(e.target.value)}
                                        placeholder="CVC"
                                        maxLength={4}
                                        className="w-full bg-[#4a4a4a] border border-[#5a5a5a] rounded-lg p-3 text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a227]"
                                    />
                                    <CreditCard className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                                </div>
                            </div>
                        </div>

                        {/* Country */}
                        <div className="relative">
                            <label className="block text-gray-400 text-sm mb-2">Country</label>
                            <button
                                onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                                className="w-full bg-[#4a4a4a] border border-[#5a5a5a] rounded-lg p-3 text-left flex items-center justify-between focus:outline-none focus:border-[#c9a227]"
                            >
                                <span className={country ? 'text-white' : 'text-gray-500'}>
                                    {country || 'Select'}
                                </span>
                                <ChevronDown className="w-5 h-5 text-gray-400" />
                            </button>
                            {showCountryDropdown && (
                                <motion.div
                                    initial={{ opacity: 0, y: -10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="absolute left-0 right-0 top-full mt-1 bg-[#4a4a4a] rounded-lg shadow-xl z-10 max-h-48 overflow-y-auto"
                                >
                                    {countries.map((c) => (
                                        <button
                                            key={c}
                                            onClick={() => { setCountry(c); setShowCountryDropdown(false); }}
                                            className="block w-full px-4 py-2 text-left text-white hover:bg-[#5a5a5a]"
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </motion.div>
                            )}
                        </div>

                        {/* Amount */}
                        <div>
                            <label className="block text-gray-400 text-sm mb-2">Amount</label>
                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white">$</span>
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    placeholder="0"
                                    className="w-full bg-[#4a4a4a] border border-[#5a5a5a] rounded-lg p-3 pl-8 pr-16 text-white placeholder-gray-500 focus:outline-none focus:border-[#c9a227]"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">(USD)</span>
                            </div>
                        </div>

                        <button className="w-full bg-[#c9a227] hover:bg-[#d4ad2e] text-black font-semibold py-3 rounded-lg transition-colors mt-4">
                            Add Funds
                        </button>
                    </motion.div>
                )}

                {paymentMethod === 'google' && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-8"
                    >
                        <p className="text-gray-400 mb-4">Continue with Google Pay</p>
                        <button className="w-full bg-white text-black font-semibold py-3 rounded-lg flex items-center justify-center gap-2">
                            <span className="font-bold">G Pay</span>
                            Pay with Google
                        </button>
                    </motion.div>
                )}

                {paymentMethod === 'apple' && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center py-8"
                    >
                        <p className="text-gray-400 mb-4">Continue with Apple Pay</p>
                        <button className="w-full bg-white text-black font-semibold py-3 rounded-lg flex items-center justify-center gap-2">
                            <span className="text-lg"></span>
                            Pay with Apple
                        </button>
                    </motion.div>
                )}
            </div>
        </ModalWrapper>
    );
}